import { useEffect, useCallback, useRef } from 'react';
import { apiFetch } from "@/lib/apiEndpoint";
import { eventBus, TaskEvents, TaskEventPayload } from '@/lib/eventBus';
import { supabase } from '@/integrations/supabase/client';
import { calculateStartTaskUpdates, calculateStopTaskUpdates, calculateToggleCompletionUpdates } from '@/logic/taskActions';
import { Task } from '@/types/task';
import { toast } from '@/lib/toast';
import { recordStateChange } from '@/hooks/useStateHistory';
import { enqueueEffect, getSyncQueue, removeEffect, recordEffectFailure, pruneStaleEffects } from '@/lib/syncQueue';
import { dbRowToTask, mapTaskToDb, TASK_COLUMNS } from '@/logic/taskMapper';
import { dbRowToLocation, mapLocationToDb } from '@/logic/locationMapper';
import { dbRowToFlow, mapFlowToDb, FLOW_COLUMNS } from '@/logic/flowMapper';
import { STATE_TRANSITION_COLUMNS } from '@/logic/stateMapper';
import { TAG_COLUMNS } from '@/logic/tagMapper';

/**
 * TaskEventHandler listens to global events and executes business logic.
 * It acts as the "Controller" in this architecture.
 */
export function TaskEventHandler(): null {
    const isProcessingRef = useRef(false);

    // Helper to fetch task if not provided in payload
    const getTask = async (payload: TaskEventPayload): Promise<Task | null> => {
        if (payload.task) return payload.task as Task;

        const { data, error } = await supabase
            .from('tasks')
            .select(TASK_COLUMNS)
            .eq('id', payload.taskId)
            .maybeSingle();

        if (error || !data) {
            console.error('TaskEventHandler: failed to fetch task', error);
            return null;
        }

        return dbRowToTask(data);
    };


    // Helper to commit to Supabase
    const applyUpdates = async (taskId: string, updates: Partial<Task>, successMsg?: string) => {
        const isOffline = !navigator.onLine;

        if (isOffline) {
            console.log('TaskEventHandler: Offline, queuing update', { taskId, updates });
            enqueueEffect(TaskEvents.UPDATE, { taskId, updates, successMsg });
            return;
        }

        try {
            const dbUpdates = mapTaskToDb(updates);

            if (Object.keys(dbUpdates).length === 0) return;

            const { data: updatedTask, error } = await supabase
                .from('tasks')
                .update(dbUpdates)
                .eq('id', taskId)
                .select()
                .maybeSingle();

            if (error) {
                console.error('TaskEventHandler UPDATE error:', error.code, error.message, error.details);
                if (error.message?.includes('Failed to fetch') || error.message?.includes('Network Error')) {
                    enqueueEffect(TaskEvents.UPDATE, { taskId, updates, successMsg });
                    toast.info('Offline: actie in wachtrij', { channel: 'tasks' });
                    return;
                }
                if (error.code === 'PGRST301' || error.message?.includes('JWT') || error.code === '401') {
                    toast.error('Sessie verlopen — herstart de app', { channel: 'tasks' });
                    return;
                }
                throw error;
            }

            if (successMsg) {
                toast.success(successMsg, { channel: 'tasks' });
            }

            // Emit updated task so listeners (useTasks) can update local state
            if (updatedTask) {
                eventBus.emit(TaskEvents.UPDATE, { taskId, task: dbRowToTask(updatedTask) });
            }

        } catch (err) {
            console.error('TaskEventHandler error:', err);
            toast.error('Actie mislukt: ' + (err as Error).message, { channel: 'tasks' });
            // Only queue if the error looks transient (network). Permanent errors
            // would otherwise create a poison-pill effect and a stuck sync banner.
            const msg = (err as Error).message || '';
            if (msg.includes('Failed to fetch') || msg.includes('Network Error') || !navigator.onLine) {
                enqueueEffect(TaskEvents.UPDATE, { taskId, updates, successMsg });
            }
        }
    };

    const processQueue = useCallback(async () => {
        if (isProcessingRef.current || !navigator.onLine) return;

        // Always prune stale entries first (recovers from older buggy queues).
        pruneStaleEffects();

        const queue = getSyncQueue();
        // Filter to task-owned effects only — the queue is shared with Stock /
        // Meal handlers, and previously this loop would silently `removeEffect`
        // any non-Task effect that fell through the if/else cascade, deleting
        // pending stock/meal mutations whichever handler ran first.
        const taskEvents = queue.filter((e) =>
            e.type === TaskEvents.UPDATE ||
            e.type === TaskEvents.CREATE ||
            e.type === TaskEvents.DELETE
        );
        if (taskEvents.length === 0) return;

        isProcessingRef.current = true;
        console.log(`TaskEventHandler: Processing ${taskEvents.length} pending actions`);

        // Sort by timestamp to preserve order
        const sortedQueue = [...taskEvents].sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );

        for (const effect of sortedQueue) {
            try {
                if (effect.type === TaskEvents.UPDATE) {
                    const { taskId, updates, successMsg } = effect.payload;

                    // Payload may already be in db (snake_case) shape when
                    // queued directly by useTasks. Detect and skip remapping.
                    const looksSnake = updates && typeof updates === 'object' &&
                        Object.keys(updates).some((k) => k.includes('_'));
                    const dbUpdates = looksSnake ? updates : mapTaskToDb(updates);

                    if (!dbUpdates || Object.keys(dbUpdates).length === 0) {
                        removeEffect(effect.id);
                        continue;
                    }

                    const { error } = await supabase
                        .from('tasks')
                        .update(dbUpdates)
                        .eq('id', taskId);

                    if (error) throw error;
                    if (successMsg) toast.success(`Hersteld: ${successMsg}`, { channel: 'tasks' });
                } else if (effect.type === TaskEvents.CREATE) {
                    const { tempId, dbInsert, optimisticTask } = effect.payload;
                    const { data, error } = await supabase
                        .from('tasks')
                        .insert(dbInsert)
                        .select()
                        .maybeSingle();

                    if (error) throw error;
                    if (data) {
                        const realTask = { ...optimisticTask, id: data.id, createdAt: new Date(data.created_at) };
                        eventBus.emit(TaskEvents.CREATED, { taskId: tempId, task: realTask });
                    }
                } else if (effect.type === TaskEvents.DELETE) {
                    const { taskId } = effect.payload;
                    const { error } = await supabase
                        .from('tasks')
                        .update({ deleted_at: new Date().toISOString() })
                        .eq('id', taskId);

                    if (error) throw error;
                }
                
                removeEffect(effect.id);
            } catch (err) {
                console.error('Failed to process sync effect:', effect, err);
                const errMsg = (err as Error)?.message || '';
                const isNetwork = errMsg.includes('Failed to fetch') || errMsg.includes('Network Error') || !navigator.onLine;
                if (isNetwork) {
                    // Network blip — stop processing; retry later without counting an attempt.
                    break;
                }
                // Bounded retry per effect — after SYNC_MAX_ATTEMPTS the effect
                // is parked in the dead-letter queue (see FailedSyncRecovery in Settings).
                recordEffectFailure(effect.id, err);
                continue;
            }
        }

        isProcessingRef.current = false;
    }, []);

    useEffect(() => {
        const handleStart = async (payload: TaskEventPayload) => {
            const task = await getTask(payload);
            if (!task) return;

            const updates = calculateStartTaskUpdates(task);
            await applyUpdates(task.id, updates, `Taak gestart: ${task.text}`);
        };

        const handleStop = async (payload: TaskEventPayload) => {
            const task = await getTask(payload);
            if (!task) return;

            const updates = calculateStopTaskUpdates(task);
            await applyUpdates(task.id, updates, `Taak gestopt: ${task.text}`);
        };

        const handleToggle = async (payload: TaskEventPayload) => {
            const task = await getTask(payload);
            if (!task) return;

            const result = calculateToggleCompletionUpdates(task);
            const updates = { ...result.updates, ...(result.extraUpdates || {}) };

            if (result.shouldToggle) {
                updates.completed = !task.completed;
                // Handle completion date
                if (updates.completed) {
                    updates.lastCompletedDate = new Date();
                    if (task.status === 'started') updates.status = 'pending';
                    // Record who completed it if available
                    if (payload.completedBy) {
                        updates.completedBy = [payload.completedBy as string];
                    }
                } else {
                    updates.lastCompletedDate = null;
                    updates.completedBy = [];
                }
            }

            await applyUpdates(task.id, updates);

            // Handle Flow State Transition
            if (updates.completed && task.flowId && task.locationId) {
                checkAndHandleFlowStateTransition(task.flowId, task.locationId);
            }

            // Handle state-transition automation (auto-transition on task completion)
            if (updates.completed) {
                checkAndHandleStateTransitionAutomation(task);
            }

            // Notify task creator when someone else completes their task
            if (updates.completed && task.createdBy && payload.completedBy) {
                notifyCreatorOnCompletion(task, payload.completedBy as string);
            }
        };

        const checkAndHandleFlowStateTransition = async (flowId: string, locationId: string) => {
            if (!navigator.onLine) return; 

            // 1. Fetch the flow and its transition rules
            const { data: flow, error: flowError } = await supabase
                .from('task_flows')
                .select(FLOW_COLUMNS)
                .eq('id', flowId)
                .single();

            if (flowError || !flow || !flow.success_state_id) return;
            const flowData = dbRowToFlow(flow);

            // 2. Fetch current location state to verify transition source
            const { data: locationRow, error: locError } = await supabase
                .from('tags')
                .select(TAG_COLUMNS)
                .eq('id', locationId)
                .single();

            if (locError || !locationRow) return;
            const location = dbRowToLocation(locationRow);
            const actualCurrentStateId = location.currentStateId;


            // 4. Check if all tasks in this flow are completed
            const { data: tasks, error: tasksError } = await supabase
                .from('tasks')
                .select('completed')
                .eq('flow_id', flowId);

            if (tasksError || !tasks) return;

            const allCompleted = tasks.every(t => t.completed);

            if (allCompleted) {
                // 5. Update the location's state
                const { error: tagUpdateError } = await supabase
                    .from('tags')
                    .update(mapLocationToDb({ currentStateId: flowData.successStateId }))
                    .eq('id', locationId);

                if (tagUpdateError) {
                    console.error('Error updating location state:', tagUpdateError);
                    return;
                }

                // Record state history for flow completion
                const { data: tagTeam } = await supabase.from('tags').select('team_id').eq('id', locationId).single();
                if (tagTeam?.team_id) {
                    recordStateChange({
                        teamId: tagTeam.team_id,
                        tagId: locationId,
                        fromStateId: actualCurrentStateId || null,
                        toStateId: flowData.successStateId ?? '',
                        triggerSource: 'flow',
                        triggerReference: flowId ?? '',
                    });
                }

                toast.success(`Status bijgewerkt naar: ${flowData.successStateId}`, { channel: 'tasks' });
            }
        };

        const checkAndHandleStateTransitionAutomation = async (task: Task) => {
            if (!navigator.onLine) return;

            if (task.targetStateId) {
                const linkedTagId = task.tagIds?.[0] || task.locationId;
                if (linkedTagId) {
                    const { data: tagData } = await supabase.from('tags').select('current_state_id, team_id').eq('id', linkedTagId).single();

                    // Look up the target state's group to determine update path
                    const { data: stateData } = await (supabase.from('states').select('state_group_id').eq('id', task.targetStateId).single() as any);
                    const stateGroupId = (stateData as any)?.state_group_id;

                    let updateError: any = null;
                    let previousStateId: string | null = tagData?.current_state_id || null;

                    if (stateGroupId) {
                        // Multi-group tag: get current state from tag_active_states for history, then upsert
                        const { data: activeState } = await (supabase
                            .from('tag_active_states' as any)
                            .select('state_id')
                            .eq('tag_id', linkedTagId)
                            .eq('state_group_id', stateGroupId)
                            .single() as any);
                        if (activeState) previousStateId = activeState.state_id;

                        const { error } = await (supabase
                            .from('tag_active_states' as any)
                            .upsert(
                                { tag_id: linkedTagId, state_group_id: stateGroupId, state_id: task.targetStateId },
                                { onConflict: 'tag_id, state_group_id' }
                            ) as any);
                        updateError = error;
                    } else {
                        // Single-state tag: update tags.current_state_id
                        const { error } = await supabase
                            .from('tags')
                            .update(mapLocationToDb({ currentStateId: task.targetStateId }))
                            .eq('id', linkedTagId);
                        updateError = error;
                    }

                    if (!updateError) {
                        if (tagData?.team_id) {
                            recordStateChange({
                                teamId: tagData.team_id,
                                tagId: linkedTagId,
                                fromStateId: previousStateId,
                                toStateId: task.targetStateId,
                                triggerSource: 'task_completion',
                                triggerReference: task.id,
                            });
                        }
                        toast.success('Status automatisch bijgewerkt naar doelstatus', { channel: 'tasks' });
                        return;
                    }
                }
            }

            if (!task.notes) return;
            const match = task.notes.match(/Transitie: ([a-f0-9-]+)/);
            if (!match) return;

            const transitionId = match[1];

            const { data: transitionRow, error } = await (supabase
                .from('state_transitions')
                .select(STATE_TRANSITION_COLUMNS)
                .eq('id', transitionId)
                .single() as any);

            if (error || !transitionRow) return;

            // Determine if the target state belongs to a state group
            const { data: targetState } = await (supabase.from('states').select('state_group_id').eq('id', transitionRow.to_state_id).single() as any);
            const groupId = (targetState as any)?.state_group_id;

            let transitionError: any = null;
            if (groupId) {
                // Multi-group: upsert into tag_active_states
                const { error: upsertErr } = await (supabase
                    .from('tag_active_states' as any)
                    .upsert(
                        { tag_id: transitionRow.tag_id, state_group_id: groupId, state_id: transitionRow.to_state_id },
                        { onConflict: 'tag_id, state_group_id' }
                    ) as any);
                transitionError = upsertErr;
            } else {
                // Single-state: update tags.current_state_id
                const { error: updateErr } = await supabase
                    .from('tags')
                    .update(mapLocationToDb({ currentStateId: transitionRow.to_state_id }))
                    .eq('id', transitionRow.tag_id);
                transitionError = updateErr;
            }

            if (transitionError) {
                console.error('Error auto-transitioning state:', transitionError);
            } else {
                recordStateChange({
                    teamId: transitionRow.team_id,
                    tagId: transitionRow.tag_id,
                    fromStateId: transitionRow.from_state_id,
                    toStateId: transitionRow.to_state_id,
                    triggerSource: 'automation',
                    triggerReference: transitionId,
                });
                toast.success('Status automatisch bijgewerkt na voltooiing', { channel: 'tasks' });
            }
        };

        const notifyCreatorOnCompletion = async (task: Task, completedByMemberId: string) => {
            if (!navigator.onLine) return;
            // Don't notify if creator completed their own task
            if (!task.createdBy || task.createdBy === completedByMemberId) return;

            try {
                // Look up creator's user_id and completer's name
                const [creatorResult, completerResult] = await Promise.all([
                    supabase.from('family_members').select('user_id').eq('id', task.createdBy).single(),
                    supabase.from('family_members').select('name').eq('id', completedByMemberId).single(),
                ]);

                const creatorUserId = creatorResult.data?.user_id;
                const completerName = completerResult.data?.name || 'Iemand';

                if (!creatorUserId) return; // Creator has no linked user account

                const { data: { session } } = await supabase.auth.getSession();
                const token = session?.access_token;
                if (!token) return;

                await apiFetch('send-push', {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        user_id: creatorUserId,
                        payload: {
                            title: `${completerName} completed your task`,
                            body: task.text,
                            url: '/',
                        },
                    }),
                });
            } catch (err) {
                console.error('Failed to send completion notification:', err);
            }
        };

        const handleCreate = async (payload: TaskEventPayload) => {
            if (!payload.task) {
                console.error('TaskEventHandler CREATE: No task in payload');
                return;
            }

            const optimisticTask = payload.task as Task;
            const tempId = payload.taskId; 
            const payloadTeamId = (payload as { teamId?: string }).teamId;

            if (!payloadTeamId) {
                console.error('TaskEventHandler: Missing teamId in CREATE payload');
                return;
            }

            const dbInsert = {
                ...mapTaskToDb(optimisticTask),
                team_id: payloadTeamId,
                status: 'pending',
                created_at: new Date().toISOString(),
                // Ensure created_by is mapped (mapTaskToDb doesn't handle it)
                ...(optimisticTask.createdBy ? { created_by: optimisticTask.createdBy } : {}),
            };
            console.log('TaskEventHandler CREATE: dbInsert', JSON.stringify(dbInsert));

            const isOffline = !navigator.onLine;
            if (isOffline) {
                enqueueEffect(TaskEvents.CREATE, { tempId, dbInsert, optimisticTask });
                return;
            }

            try {
                const { data, error } = await supabase
                    .from('tasks')
                    .insert(dbInsert)
                    .select()
                    .maybeSingle();

                if (error) {
                    console.error('TaskEventHandler CREATE error:', error.code, error.message, error.details, error.hint);
                    if (error.message?.includes('Failed to fetch') || error.message?.includes('Network Error')) {
                        enqueueEffect(TaskEvents.CREATE, { tempId, dbInsert, optimisticTask });
                        toast.info('Offline: taak in wachtrij', { channel: 'tasks' });
                        return;
                    }
                    if (error.code === 'PGRST301' || error.message?.includes('JWT') || error.code === '401') {
                        toast.error('Sessie verlopen — herstart de app', { channel: 'tasks' });
                        return;
                    }
                    throw error;
                }

                if (data) {
                    const realTask = {
                        ...optimisticTask,
                        id: data.id,
                        createdAt: new Date(data.created_at),
                    };

                    eventBus.emit(TaskEvents.CREATED, {
                        taskId: tempId, 
                        task: realTask
                    });

                    // Completing a recurring task inserts an already-completed
                    // "completion instance" snapshot — that's not a new to-do, so
                    // don't announce it as a created task.
                    if (!realTask.completed) {
                        toast.success(`Taak aangemaakt: ${realTask.text}`, { channel: 'tasks' });
                    }
                }

            } catch (err) {
                console.error('Create task failed:', err);
                toast.error('Kon taak niet opslaan: ' + (err as Error).message, { channel: 'tasks' });
                enqueueEffect(TaskEvents.CREATE, { tempId, dbInsert, optimisticTask });
            }
        };

        const handleUpdate = async (payload: TaskEventPayload) => {
            const taskId = payload.taskId;
            if (!taskId) return;
            // Payload itself contains the updates
            const { taskId: _, task: __, ...updates } = payload;
            await applyUpdates(taskId, updates as any);
        };

        const handleDelete = async (payload: TaskEventPayload) => {
            const taskId = payload.taskId;
            if (!taskId) return;

            const isOffline = !navigator.onLine;
            if (isOffline) {
                console.log('TaskEventHandler: Offline, queuing delete', { taskId });
                enqueueEffect(TaskEvents.DELETE, { taskId });
                return;
            }

            try {
                const { error } = await supabase
                    .from('tasks')
                    .update({ deleted_at: new Date().toISOString() })
                    .eq('id', taskId);

                if (error) {
                    console.error('TaskEventHandler DELETE error:', error);
                    if (error.message?.includes('Failed to fetch') || error.message?.includes('Network Error')) {
                        enqueueEffect(TaskEvents.DELETE, { taskId });
                        toast.info('Offline: verwijdering in wachtrij', { channel: 'tasks' });
                        return;
                    }
                    throw error;
                }
            } catch (err) {
                console.error('Delete task failed:', err);
                toast.error('Kon taak niet verwijderen: ' + (err as Error).message, { channel: 'tasks' });
                enqueueEffect(TaskEvents.DELETE, { taskId });
            }
        };

        eventBus.on(TaskEvents.START, handleStart);
        eventBus.on(TaskEvents.STOP, handleStop);
        eventBus.on(TaskEvents.TOGGLE, handleToggle);
        eventBus.on(TaskEvents.UPDATE, handleUpdate);
        eventBus.on(TaskEvents.CREATE, handleCreate);
        eventBus.on(TaskEvents.DELETE, handleDelete);

        window.addEventListener('online', processQueue);

        processQueue();

        return () => {
            eventBus.off(TaskEvents.START, handleStart);
            eventBus.off(TaskEvents.STOP, handleStop);
            eventBus.off(TaskEvents.TOGGLE, handleToggle);
            eventBus.off(TaskEvents.UPDATE, handleUpdate);
            eventBus.off(TaskEvents.CREATE, handleCreate);
            eventBus.off(TaskEvents.DELETE, handleDelete);
            window.removeEventListener('online', processQueue);
        };
    }, [processQueue]);

    return null; 
}
